import { z } from 'zod'
import { apiFetch } from '../client'
import {
  catalogPlanSchema,
  currencySchema,
  planPriceSchema,
  type CatalogPlan,
  type Currency,
  type PlanPrice,
} from '../schemas/catalog'

/**
 * Public catalog reads (no auth). Rate-limited at 60/min — consumers cache
 * with staleTime ≥ 60s in TanStack Query (UI-RECOMMENDATIONS §5.7).
 */
/**
 * The reserved productSlug for FenceAuth's own three tiers — platform
 * pricing has no owning tenant, so it can't live in the ordinary
 * tenant-product plan_catalog table (confirmed backend contract). Calling
 * GET /catalog/plans with NO productSlug does not mean "FenceAuth's own
 * plans" — it falls through to the legacy per-tenant-product catalog.
 */
export const PLATFORM_PRODUCT_SLUG = 'fenceauth-platform'

export const catalogApi = {
  /** FenceAuth's own platform plans — pricing page, onboarding, P4 pickers. */
  async listPlans(): Promise<CatalogPlan[]> {
    // catalogApi.plans — NOT `this.plans`: listPlans is called as a
    // detached queryFn reference (queryFn: catalogApi.listPlans), so
    // `this` would be undefined here.
    return catalogApi.plans(PLATFORM_PRODUCT_SLUG)
  },

  /** A tenant product's plans (P4.4) — always send productSlug from the portal. */
  async plans(productSlug: string): Promise<CatalogPlan[]> {
    const data = await apiFetch<unknown>('/api/v1/catalog/plans', { query: { productSlug } })
    return z.array(catalogPlanSchema).parse(data)
  },

  async listCurrencies(): Promise<Currency[]> {
    const data = await apiFetch<unknown>('/api/v1/catalog/currencies')
    return z.array(currencySchema).parse(data)
  },

  /** Every plan price offered in one currency (missing plans fall back to base price). */
  async listPlanPricesForCurrency(currencyCode: string): Promise<PlanPrice[]> {
    const data = await apiFetch<unknown>(`/api/v1/catalog/currencies/${currencyCode}/plans`)
    return z.array(planPriceSchema).parse(data)
  },
}
